import lodash from 'lodash'

import type {
  FileMetaSchemaFragment,
  FilesGroupsFragment,
  ProjectGroupListSchemaFragment,
  ProjectListSchemaFragment,
} from 'gql/__generated__/graphql'
import type { FinderItem } from 'store/models'
import { EntryTypeName } from 'store/models'

import type { IEmptyProps } from '../Empty'
import type { IHighlighterProps } from '../Highlighter'
import type { KitSize } from '../KitTypes'
import { DEFAULT_EMPTY_CONFIG } from './constants'
import type { ColumnState, ColumnType, Highlighted } from './types'

export const getFinderRecordName = (
  record: FileMetaSchemaFragment | FilesGroupsFragment,
): string => {
  if (!('type' in record)) {
    return record.name
  }

  const stringifiedName = record.name.toString()
  const lastIndexOfDot = stringifiedName.lastIndexOf('.')

  return stringifiedName.slice(0, lastIndexOfDot)
}

export const getNameString = (
  name?: string | React.ReactElement<IHighlighterProps> | null,
): string | null | undefined => {
  if (!name) {
    return name
  }

  if (typeof name === 'string') {
    return name
  }

  return name?.props.text
}

const FOLDER_TYPES = [EntryTypeName.projectGroup, EntryTypeName.fileMetaGroup]

type ItemsForGrouping = (
  | FinderItem
  | Highlighted<ProjectListSchemaFragment | ProjectGroupListSchemaFragment>
)[]

export const getDataGrouping = (items?: ItemsForGrouping) => {
  const { groups, elements } = lodash.groupBy(items, (i) =>
    FOLDER_TYPES.includes(i?.__typename as EntryTypeName) ? 'groups' : 'elements',
  )

  return {
    groupIds: groups?.length ? groups.map((i) => i.id) : null,
    elementIds: elements?.length ? elements.map((i) => i.id) : null,
  }
}

export const getColumnKey = (dataIndex: string | string[]) => {
  if (typeof dataIndex === 'string') {
    return dataIndex
  }

  return dataIndex.join('.')
}

export const getColumnIdentity = (column: Pick<ColumnType<unknown>, 'key' | 'dataIndex'>) =>
  column.key ?? getColumnKey(column.dataIndex)

const getColumnDefaultState = (
  column: Pick<ColumnType<unknown>, 'key' | 'dataIndex' | 'width'>,
): ColumnState | undefined => ({
  key: getColumnIdentity(column),
  visible: true,
  width: column.width,
})

export const getColumnsDefaultState = (columns: Pick<ColumnType<unknown>, 'key' | 'dataIndex'>[]) =>
  columns.map(getColumnDefaultState).filter(Boolean) as ColumnState[]

export const getColumnTitle = (
  columnKey: string,
  columns: Pick<ColumnType<unknown>, 'key' | 'dataIndex' | 'title'>[],
) => {
  const column = columns.find(
    (column) => column.dataIndex && getColumnIdentity(column) === columnKey,
  )
  return column?.title
}

interface IErrorItem {
  isError?: boolean
  text?: string | JSX.Element
  icon?: IEmptyProps['icon']
  size?: KitSize
}

type IErrorParams = {
  search?: IErrorItem
  disabled?: IErrorItem
  empty?: IErrorItem
}

export const getTableErrorInfo = (errorParams: IErrorParams): IEmptyProps => {
  const errosArray = Object.keys(DEFAULT_EMPTY_CONFIG) as (keyof typeof DEFAULT_EMPTY_CONFIG)[]
  const errorKey = errosArray.find((er) => errorParams[er]?.isError) || 'empty'

  return { ...DEFAULT_EMPTY_CONFIG[errorKey], ...errorParams[errorKey] }
}

export const mergeColumnSettings = <RecordType,>(
  baseColumns: ColumnType<RecordType>[],
  userSettings: ColumnState[],
): ColumnType<RecordType>[] => {
  if (!userSettings || !userSettings.length) {
    return baseColumns
  }

  return userSettings.map((userCol) => {
    const baseCol = baseColumns.find((col) => getColumnIdentity(col) === userCol.key)
    if (!baseCol) {
      console.error('Cannot find column', userCol.key)
    }
    return {
      ...baseCol,
      dataIndex: baseCol?.dataIndex || '',
      width: userCol.width,
      hidden: userCol.visible === false,
    }
  })
}

export const extractUserSettingsFromColumns = <RecordType,>(
  columns: ColumnType<RecordType>[],
): ColumnState[] => {
  return columns.map((col) => ({
    key: getColumnIdentity(col),
    width: col.width,
    visible: !col.hidden,
  }))
}
